export type ShortcutModifier = 'none' | 'alt' | 'ctrl' | 'shift';

export type ShortcutBinding = {
  modifier: ShortcutModifier;
  /** Lowercase a–z */
  key: string;
};

export const SHORTCUT_ACTION_IDS = [
  'delayBothDown',
  'delayBothUp',
  'seekPrev',
  'seekRepeat',
  'seekNext',
  'toggleMain',
  'toggleSecond',
  'dualMain',
  'dualSecond',
  'reader',
  'mic',
  'copy',
  'delayMainDown',
  'delayMainUp',
  'delaySecondDown',
  'delaySecondUp',
  'dictationHint',
  'dictationPlayWord',
  'speakingRecord',
  'speakingRecordToggle',
  'speakingReplay',
] as const;

export type ShortcutActionId = (typeof SHORTCUT_ACTION_IDS)[number];

export type ShortcutBindings = Record<ShortcutActionId, ShortcutBinding>;

/** Mirrors today's hardcoded Alt/Ctrl/Shift letter bindings (B-SHORT-*). */
export const DEFAULT_SHORTCUT_BINDINGS: ShortcutBindings = {
  delayBothDown: { modifier: 'alt', key: 'q' },
  delayBothUp: { modifier: 'alt', key: 'e' },
  seekPrev: { modifier: 'alt', key: 'a' },
  seekRepeat: { modifier: 'alt', key: 's' },
  seekNext: { modifier: 'alt', key: 'd' },
  toggleMain: { modifier: 'alt', key: 'z' },
  toggleSecond: { modifier: 'alt', key: 'x' },
  dualMain: { modifier: 'alt', key: 'c' },
  dualSecond: { modifier: 'alt', key: 'v' },
  reader: { modifier: 'alt', key: 'l' },
  mic: { modifier: 'alt', key: 'm' },
  copy: { modifier: 'ctrl', key: 'c' },
  delayMainDown: { modifier: 'shift', key: 'q' },
  delayMainUp: { modifier: 'shift', key: 'e' },
  delaySecondDown: { modifier: 'ctrl', key: 'q' },
  delaySecondUp: { modifier: 'ctrl', key: 'e' },
  dictationHint: { modifier: 'alt', key: 'h' },
  dictationPlayWord: { modifier: 'alt', key: 'w' },
  speakingRecord: { modifier: 'alt', key: 'f' },
  speakingRecordToggle: { modifier: 'alt', key: 'r' },
  speakingReplay: { modifier: 'alt', key: 'g' },
};

const MODIFIERS = new Set<ShortcutModifier>(['none', 'alt', 'ctrl', 'shift']);

function isLetterKey(key: unknown): key is string {
  return typeof key === 'string' && /^[a-z]$/.test(key);
}

function normalizeBinding(raw: unknown, fallback: ShortcutBinding): ShortcutBinding {
  if (!raw || typeof raw !== 'object') return { ...fallback };
  const candidate = raw as Partial<ShortcutBinding>;
  const modifier = MODIFIERS.has(candidate.modifier as ShortcutModifier)
    ? (candidate.modifier as ShortcutModifier)
    : fallback.modifier;
  const key = isLetterKey(typeof candidate.key === 'string' ? candidate.key.toLowerCase() : '')
    ? (candidate.key as string).toLowerCase()
    : fallback.key;
  return { modifier, key };
}

/** Fill missing / invalid actions from defaults (safe for storage.sync blobs). */
export function mergeShortcutBindings(
  stored: Partial<ShortcutBindings> | null | undefined,
): ShortcutBindings {
  const out = { ...DEFAULT_SHORTCUT_BINDINGS };
  if (!stored || typeof stored !== 'object') return out;
  for (const id of SHORTCUT_ACTION_IDS) {
    out[id] = normalizeBinding(stored[id], DEFAULT_SHORTCUT_BINDINGS[id]);
  }
  return out;
}

export function bindingSignature(binding: ShortcutBinding): string {
  return `${binding.modifier}+${binding.key}`;
}
